/**
 * Dashboard KPI hooks.
 *
 * Single source of truth: authenticated sessions ONLY ever see API data.
 *  - loading → `isLoading` (the page renders skeletons, never placeholder numbers)
 *  - error   → `isError` with zeroed/empty data (the page shows a warning banner)
 */

import { useQuery, type QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import { useAuthStore } from "@/features/auth/auth.store";
import {
  dashboardApi,
  getMonthlySeries,
  getRecentActivity,
  type ActivityItem,
  type DashboardKpis,
  type DashboardPeriod,
  type MonthlySeriesPoint,
} from "./dashboard.api";

type DataSource = "api" | "mock";

const EMPTY_KPIS: DashboardKpis = {
  collectedDzd: 0,
  expensesDzd: 0,
  netProfitDzd: 0,
  receivablesDzd: 0,
  overdueInvoices: { count: 0, value: 0 },
  treasury: {},
  adSpendUsd: 0,
  walletBalanceUsd: 0,
  lowWallets: 0,
  clientsActive: 0,
  pendingApprovals: 0,
};

export function useDashboardSeries(months: number) {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.dashboardSeries(months),
    queryFn: () => getMonthlySeries(months),
    enabled: authed,
  });
  if (query.data) return { series: query.data.series, isLoading: false, isError: false };
  if (query.isError) return { series: [] as MonthlySeriesPoint[], isLoading: false, isError: true };
  return { series: [] as MonthlySeriesPoint[], isLoading: query.isLoading, isError: false };
}

export interface UseDashboardResult {
  kpis: DashboardKpis;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

/**
 * Warm the dashboard caches for the landing page (startup warmup — see
 * layout/useWarmupCoreData). "6m" matches DashboardPage's default period.
 */
export function prefetchDashboard(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.prefetchQuery({ queryKey: queryKeys.dashboard("6m"), queryFn: () => dashboardApi.get("6m") }),
    qc.prefetchQuery({ queryKey: queryKeys.recentActivity, queryFn: getRecentActivity }),
  ]).then(() => undefined);
}

export function useRecentActivity(): {
  activities: ActivityItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.recentActivity,
    queryFn: getRecentActivity,
    enabled: authed,
  });
  if (query.data) return { activities: query.data.items, isLoading: false, isError: false };
  if (query.isError) return { activities: [] as ActivityItem[], isLoading: false, isError: true };
  return { activities: [] as ActivityItem[], isLoading: query.isLoading, isError: false };
}

export function useDashboardReport(period: DashboardPeriod): UseDashboardResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.dashboard(period),
    queryFn: () => dashboardApi.get(period),
    enabled: authed,
  });

  if (query.data)
    return {
      kpis: query.data.kpis,
      isLoading: false,
      isError: false,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      kpis: EMPTY_KPIS,
      isLoading: false,
      isError: true,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  return {
    kpis: EMPTY_KPIS,
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}
